<script setup lang="ts">
import type { LikeableResourceGroup, Category } from '@/models'
import OpenClose from '@/components/OpenClose.vue'
import ResourceCard from '@/components/resources/ResourceCard.vue'
import LikeButton from '@/components//LikeButton.vue'
import RightArrow from '@/assets/rightArrow.svg'
import { ref } from 'vue'
//import { i18n } from '@/i18n'
//const t = i18n.global.t

const props = defineProps<{
  category: Category
  stage: number
  resourceGroup: LikeableResourceGroup
  liked: boolean
  likeMode: 'build' | 'like' | 'none' // Controls the like button
  mode: 'build' | 'regular' // Controls general look and feel
}>()

const emit = defineEmits<{
  (e: 'like', value: string): void
}>()

const open = ref(false)
const menu = ref(false)
</script>

<template>
  <div
    class="inline-flex flex-col items-center text-center w-full bg-white rounded-2xl border border-[#DBD2D2] shadow-sm"
  >
    <!-- Group header: expand/collapse toggle and optional like button -->
    <div class="w-full flex flex-row items-center justify-between px-8 py-4">
      <p class="text-2xl sm:text-3xl truncate">{{ resourceGroup.title }}</p>
      <div class="flex flex-row items-center">
        <div
          @click="open = !open"
          class="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer bg-[#042E2C]"
        >
          <img
            :src="RightArrow"
            class="transition-all duration-500 ease-out w-3 h-3 invert"
            :class="[open ? 'rotate-270' : 'rotate-90']"
          />
        </div>
        <div
          v-if="likeMode !== 'none'"
          @click="emit('like', props.resourceGroup.documentId)"
          class="ml-4 h-12 rounded-full flex items-center justify-center cursor-pointer bg-white"
          :class="liked ? '' : ''"
        >
          <LikeButton class="w-6 h-6" :liked="liked" />
        </div>

        <div
          v-if="likeMode !== 'none'"
          @pointerdown="menu = true"
          @pointerup="menu = false"
          class="ml-4 h-12 rounded-full flex items-center justify-center cursor-pointer"
        >
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Collapsible content: description and resource cards grid -->
    <OpenClose
      :open="open"
      class="w-full bg-white"
      :class="[open ? 'items-center border-t border-[#DBD2D2] rounded-b-2xl' : '']"
    >
      <p class="m-8 font-normal text-xl sm:text-2xl text-left">
        {{ resourceGroup.description }}
      </p>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-max max-sm:mx-auto gap-3 justify-items-center mb-4 px-4"
      >
        <ResourceCard
          v-for="rc of resourceGroup.resources"
          :key="rc.documentId"
          :resource="rc"
          :liked="rc.liked"
          :category="category"
          :like-mode="likeMode"
          :mode="mode"
          @like="(rID: string) => emit('like', rID)"
        />
      </div>
    </OpenClose>
  </div>
</template>
